/**
 * useWorkflowDeadLetters - Fila de erros definitivos do Workflow 2.0 (Fase 7)
 *
 * Leitura por `get_workflow_dead_letters` e resolução auditada por
 * `resolve_workflow_dead_letter` (reprocessar, ignorar, cancelar processo ou
 * compensar). Toda validação de tenant e de permissão é feita no banco.
 *
 * @version 1.1.0
 * RELEASE: 2026-10-06 v1.1.0 - Filtro opcional por processo e paginação; item traz
 *          processo e registro de origem (RPC v1.1.0).
 * RELEASE: 2026-08-30 v1.0.0 - Criação inicial (Fase 7)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

const logger = createLogger('useWorkflowDeadLetters');

/** Item da fila de erros definitivos */
export interface WorkflowDeadLetter {
  id: string;
  instance_id: string | null;
  action_code: string;
  action_label: string | null;
  error_message: string | null;
  error_code: string | null;
  error_class: string;
  attempts: number;
  is_compensation: boolean;
  status: string;
  resolution_reason: string | null;
  resolved_at: string | null;
  created_at: string;
  /** v1.1.0: contexto do processo e do registro de origem */
  definition_id?: string | null;
  definition_name?: string | null;
  root_entity_type?: string | null;
  root_entity_id?: string | null;
}

/** Ações de resolução aceitas pela RPC */
export type WorkflowDlqAction = 'reprocessar' | 'ignorar' | 'cancelar_instancia' | 'compensar';

export function useWorkflowDeadLetters(
  status: string = 'aberto',
  opcoes: { definitionId?: string | null; limit?: number; offset?: number } = {},
) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['workflow-dead-letters', tenantId, status, opcoes],
    enabled: !!tenantId,
    staleTime: 15_000,
    queryFn: async (): Promise<{ total: number; itens: WorkflowDeadLetter[] }> => {
      const { data, error } = await (supabase.rpc as any)('get_workflow_dead_letters', {
        p_status: status,
        p_limit: opcoes.limit ?? 50,
        p_offset: opcoes.offset ?? 0,
        p_definition_id: opcoes.definitionId ?? null,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao carregar erros do workflow', { error: error.message });
        throw error;
      }
      return (data ?? { total: 0, itens: [] }) as { total: number; itens: WorkflowDeadLetter[] };
    },
  });

  /** Resolve o erro com justificativa obrigatória (auditada no banco) */
  const resolve = useMutation({
    mutationFn: async (input: { id: string; acao: WorkflowDlqAction; justificativa: string }) => {
      const { data, error } = await (supabase.rpc as any)('resolve_workflow_dead_letter', {
        p_dead_letter_id: input.id,
        p_acao: input.acao,
        p_justificativa: input.justificativa,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-dead-letters', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['deal-workflow'] });
      queryClient.invalidateQueries({ queryKey: ['workflow-overview-kpis'] });
      toast.success('Erro do processo resolvido');
    },
    onError: (error: any) => {
      logger.error('Erro ao resolver item da DLQ', { error: error?.message });
      toast.error(error?.message || 'Não foi possível resolver o erro do processo');
    },
  });

  return {
    itens: query.data?.itens ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    refetch: query.refetch,
    resolve,
  };
}
